import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../api/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { LockClosedIcon, ArrowLeftIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import bluelogo from '../assets/bluelogo.png';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setInvalidToken(true);
      return;
    }
    if (!newPassword || !newPasswordConfirm) {
      setError('Tüm alanları doldurun');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('Şifreler eşleşmiyor');
      return;
    }
    if (newPassword.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await authAPI.resetPassword(token, newPassword, newPasswordConfirm);

      if (response?.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2500);
      } else {
        setError(response?.message || 'Şifre sıfırlanamadı. Link süresi dolmuş olabilir.');
      }
    } catch (err) {
      setError(err.message || 'İşlem sırasında bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  if (invalidToken || !token) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                Geçersiz Link
              </CardTitle>
              <CardDescription className="text-gray-600 dark:text-gray-400">
                Bu link geçersiz veya süresi dolmuş. Lütfen yeni bir şifre sıfırlama talebi oluşturun.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/forgot-password" className="w-full">
                <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  Yeni link talep et
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                <CheckCircleIcon className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                Şifre Güncellendi
              </CardTitle>
              <CardDescription className="text-gray-600 dark:text-gray-400">
                Şifreniz başarıyla güncellendi. Giriş sayfasına yönlendiriliyorsunuz...
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                Giriş yap
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="mx-auto w-28 h-28 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center mb-6 shadow-md border border-gray-200 dark:border-gray-600 p-3">
              <img src={bluelogo} alt="HelpDesk Logo" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              Yeni Şifre Belirle
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Yeni şifrenizi girin ve tekrar edin.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {error && (
                <div className="flex items-center p-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-3">
                <Label htmlFor="new_password" className="text-sm font-medium text-gray-900 dark:text-white">
                  Yeni Şifre
                </Label>
                <div className="relative group">
                  <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <Input
                    id="new_password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-11 h-12 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="new_password_confirm" className="text-sm font-medium text-gray-900 dark:text-white">
                  Yeni Şifre (Tekrar)
                </Label>
                <div className="relative group">
                  <LockClosedIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <Input
                    id="new_password_confirm"
                    type="password"
                    placeholder="••••••••"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    className="pl-11 h-12 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                    required
                    disabled={isLoading}
                    autoComplete="new-password"
                    minLength={6}
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Güncelleniyor...</span>
                  </div>
                ) : (
                  'Şifreyi Güncelle'
                )}
              </Button>
              <Link
                to="/login"
                className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-400"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Giriş sayfasına dön
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
